/* eslint-disable max-lines */
import { darkThemeStyles, JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import { createStyles } from '@material-ui/core';

const styles = (theme: JupiterTheme) => {
  const { palette } = theme;
  return createStyles({
    ...darkThemeStyles,
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
    fadeIn: {
      animation: '$fadeIn',
      animationDuration: '0.35s',
      height: '100%',
      width: '100%',
    },
    blurred: {
      filter: `blur(6px)`,
    },
    tabContainer: {
      flex: 1,
      padding: '4px 48px 24px',
    },
    backdropTransparent: {
      display: 'none',
    },
    hover: {
      cursor: 'pointer',
    },
    drawerPaper: {
      zIndex: 2,
      padding: `${theme.spacing(5)}px`,
      top: 73,
    },
    headerLayout: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.5),
      '&:not(:first-child)': {
        marginTop: theme.spacing(4),
      },
      '& *': {
        outline: 'none',
      },
      '& $button': {
        borderColor: palette.primary.contrastText,
        color: palette.primary.contrastText,
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        '&:hover, &:focus': {
          borderColor: palette.primary.main,
          color: palette.primary.main,
        },
      },
      '& h3': {
        lineHeight: 1,
        marginTop: -1,
      },
    },
    usersAndAccess: {
      height: '100%',
      width: '100%',
    },
    lazyLoadedListContainer: {
      position: 'relative',
      height: 'calc(100% - 90px)',
    },
    listCustomScrollbar: {
      '&::-webkit-scrollbar': {
        background: `rgba(8, 34, 76, .875)`,
        borderRadius: '0 4px 4px 0',
      },
      '&::-webkit-scrollbar-thumb': {
        background: `rgba(0, 6, 42, .875)`,
        borderRadius: '0 4px 4px 0',
      },
    },
    lazyLoadedList: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      marginTop: '40px',
      width: '100%',
    },
    lazyLoadedListLoaderContainer: {
      position: 'absolute',
      width: '100%',
      bottom: 0,
      background:
        'linear-gradient(to bottom, rgba(0,0,0,0%) 0%,rgba(0,0,0,0.65) 100%)',
      display: 'flex',
      paddingTop: 64,
      paddingBottom: 64,
      justifyContent: 'center',
    },
    lazyLoadedListCircularProgress: {
      color: 'white',
    },
    listItem: {
      paddingLeft: 0,
      paddingRight: 0,
      cursor: 'pointer',
    },
    divider: {
      background: 'rgba(0,0,0, 0.8)',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    groupAvatar: {
      background: 'rgba(0, 0, 0, 0.6)',
      border: 'none',
      color: theme.palette.primary.contrastText,
      width: 48,
      height: 48,
    },
    listActionIcon: {
      color: palette.primary.contrastText,
      '&:hover, &:focus': {
        background: palette.primary.main,
      },
      '&:last-child': {
        marginRight: -theme.spacing(1.5),
      },
    },
    primaryInvitation: {
      color: palette.coolGrey[50],
      fontSize: 16,
      textTransform: 'none',
    },
    secondaryInvitation: {
      color: palette.coolGrey[300],
      fontSize: 14,
    },
    pageTitle: {
      background: palette.coolGrey[100],
      display: 'flex',
      flexFlow: 'column',
      color: palette.primary.dark,
      fontWeight: 400,
      fontSize: '2rem',
      letterSpacing: '-0.05rem',
      textAlign: 'center',
      padding: `${theme.spacing(8)}px 0 ${theme.spacing(0.25)}px`,
    },
    pageTitleIcon: {
      color: palette.coolGrey[300],
      margin: '0 auto 8px',
      height: 70,
      width: 70,
      position: 'relative',
      top: 2,
      strokeWidth: 0.9,
    },
    searchContainer: {
      background: palette.coolGrey[100],
      padding: `${theme.spacing(0.25)}px 0 ${theme.spacing(10)}px`,
    },
    searchPaper: {
      display: 'flex',
      alignItems: 'center',
      background: palette.grey[50],
      margin: `${theme.spacing(4)}px auto`,
      padding: theme.spacing(2),
      width: '50%',
    },
    searchIcon: {
      color: palette.coolGrey[300],
      marginRight: 8,
      width: 32,
      height: 32,
    },
    gridItemSearch: {
      background: palette.grey[50],
    },
    searchInput: {
      fontSize: 20,
      '&::before, &::after': {
        display: 'none',
      },
    },
    stepLabel: {
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: palette.secondary.main,
      letterSpacing: 'normal',
      fontSize: '1rem',
      fontWeight: 400,
      textAlign: 'center',
      lineHeight: 1,
      margin: '0 auto',
      width: '100%',
      padding: `${theme.spacing(2)}px ${theme.spacing(4)}px ${theme.spacing(
        1.5
      )}px`,
      flex: '0 0 auto',
      height: 25,
    },
    stepTitle: {
      background: 'rgba(0, 0, 0, 0.5)',
      borderRadius: '4px 4px 0 0',
      color: palette.grey[50],
      fontSize: '2rem',
      fontWeight: 900,
      paddingBottom: theme.spacing(7),
      paddingTop: theme.spacing(5),
      textAlign: 'center',
      letterSpacing: '-0.05rem',
      position: 'relative',
      '&:after': {
        backgroundPosition: '50% 100%',
        background: `url(/img/curve-divider.svg) no-repeat`,
        backgroundSize: '110%',
        display: 'block',
        width: '100%',
        height: 47,
        position: 'absolute',
        content: `''`,
        bottom: -2,
      },
    },
    stepDesc: {
      color: palette.coolGrey[400],
      textAlign: 'center',
      margin: `${theme.spacing(1.25)}px ${theme.spacing(6)}px ${theme.spacing(
        10
      )}px`,
    },
    iconTextWrapper: {
      display: 'inline-block',
    },
    inlineTextIcon: {
      width: 17,
      height: 17,
      position: 'relative',
      top: 3,
    },
    appIconAssetsInventory: {
      position: 'relative',
      top: 2.5,
    },
    gridItemStep: {
      background: palette.grey[50],
      display: 'flex',
      flexFlow: 'column',
      border: `solid 1px rgba(255, 255, 255, .5)`,
      borderRadius: 4,
      margin: `-${theme.spacing(8)}px ${theme.spacing(2)}px ${theme.spacing(
        2
      )}px `,
      maxHeight: 480,
      paddingBottom: theme.spacing(3),
      position: 'relative',
      '&::before': {
        display: 'flex',
        alignSelf: 'center',
        boxShadow: '0 -6px 25px -8px rgb(0, 6, 42, 0.25)',
        content: `''`,
        position: 'absolute',
        margin: '0 auto',
        width: `calc(100% - 80px)`,
        top: 0,
        height: 20,
        zIndex: 1,
      },
      '&::after': {
        display: 'flex',
        alignSelf: 'center',
        boxShadow: '0 10px 25px -8px rgb(0, 6, 42, 0.25)',
        content: `''`,
        position: 'absolute',
        margin: '0 auto',
        width: `calc(100% - 80px)`,
        bottom: 0,
        height: 20,
        zIndex: 1,
      },
    },
    card: {
      display: 'flex',
      boxShadow: 'none',
      background: 'none',
      justifyContent: 'center',
      flexFlow: 'column',
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px ${theme.spacing(
        1
      )}px`,
      margin: '0 auto',
    },
    appContent: {
      borderRadius: 48,
      border: 'solid 1px ' + palette.coolGrey[100],
      background: palette.grey[100],
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      padding: `${theme.spacing(2)}px ${theme.spacing(4)}px`,
      position: 'relative',
      '& p': {
        marginTop: '1rem',
      },
      '&:hover': {
        color: palette.grey[50],
        background: palette.background.paper,
        cursor: 'pointer',
      },
      '& a': {
        textDecoration: 'none',
        color: palette.coolGrey[800],
      },
    },
    cardTitle: {
      display: 'flex',
      justifyContent: 'center',
      fontWeight: 'normal',
      fontSize: '1rem',
      lineHeight: 1.25,
      color: palette.coolGrey[700],
    },
    iconContainer: {
      display: 'flex',
      height: theme.spacing(5),
      width: theme.spacing(5),
      marginRight: 24,
    },
    icon: {
      strokeWidth: 1,
      fill: theme.palette.primary.dark,
      height: '100%',
      width: '100%',
    },
    iconJ1App: {
      fill: 'none',
      stroke: theme.palette.primary.dark,
    },
    spinnerCenter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      padding: '40px',
      height: '100%',
      width: '100%',
      textAlign: 'center',
    },
    groupPanelSubheaderText: {
      color: theme.palette.coolGrey[50],
      fontSize: '1.5rem',
      fontWeight: 900,
    },
    groupPanelContentText: {
      color: theme.palette.coolGrey[50],
      fontSize: '1em',
      fontWeight: 900,
    },
    toolbarButton: {
      opacity: 0.5,
      '&:hover': {
        opacity: '0.75' as any,
      },
    },
    tabs: {
      background: 'rgba(0, 0, 0, 0.5)',
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      margin: '0px 40px 24px',
      minHeight: 60,
    },
    tab: {
      color: theme.palette.primary.contrastText,
      marginLeft: 0,
      marginRight: 0,
      minHeight: 60,
    },
    tabAlert: {
      color: theme.palette.primary.contrastText,
      marginLeft: 0,
      marginRight: 0,
      minHeight: 60,
      position: 'relative',
      '& span > svg': {
        top: 2,
        left: -1,
        position: 'relative',
      },
      '& span': {
        position: 'absolute',
        zIndex: 2,
      },
      '&::before': {
        borderRadius: '50%',
        border: 'solid 1px' + theme.palette.coolGrey[500],
        content: `''`,
        width: 40,
        height: 40,
        position: 'absolute',
        zIndex: 1,
      },
      '&::after': {
        borderRadius: '50%',
        background: theme.palette.red[600],
        content: `''`,
        width: 10,
        height: 10,
        position: 'absolute',
        left: '55%',
        bottom: 10,
        zIndex: 3,
      },
    },
    tabText: {
      flex: 1,
      '&&': {
        fontSize: 14,
        fontWeight: 700,
      },
    },
    tabHeader: {
      color: theme.palette.primary.main,
      marginBottom: 8,
      textAlign: 'center',
    },
    dialActionButton: {
      background: theme.palette.coolGrey[50],
      color: theme.palette.coolGrey[600],
    },
    checkbox: {
      marginTop: theme.spacing(2),
    },
    permissionsContainer: {
      height: '100%',
    },
    permissionsScrollBody: {
      overflow: 'scroll',
    },
  });
};

export default styles;
